import {
  ApolloClient,
  ApolloLink,
  HttpLink,
  InMemoryCache,
  NormalizedCacheObject,
  Operation,
  ServerError,
} from "@apollo/client";
import { ErrorResponse, onError } from "@apollo/client/link/error";
import ActionCableLink from "graphql-ruby-client/subscriptions/ActionCableLink";
import { createConsumer } from "@rails/actioncable";
import { OperationDefinitionNode } from "graphql/language";
import {
  CANCELLATION_REQUEST_ERRORS,
  DEFAULT_APOLLO_CLIENT_OPTIONS,
  GRAPHQL_PATH,
  STATUS_CODES_TO_BE_NOTIFIED,
} from "../constants/apolloClient";
import { notifyErrorFromResponse } from "../utils/notify";
import { ServerErrorResult } from "../types/error";
import type { NetworkError } from "@apollo/client/errors";
import { GraphQLFormattedError } from "graphql/index";
import { redirectToLoginIfSessionExpired } from "../utils/sessionExpiryRedirect";

const httpLink = new HttpLink({
  uri: `${window.urlPrefix}${GRAPHQL_PATH}`,
  credentials: "same-origin",
});

const cable = createConsumer(`${window.urlPrefix}/cable`);

const hasSubscriptionOperation = ({ query: { definitions } }: Operation) => {
  return (definitions as OperationDefinitionNode[]).some(
    ({ kind, operation }) => kind === "OperationDefinition" && operation === "subscription"
  );
};

const errorLink = onError(({ graphQLErrors, networkError, operation }: ErrorResponse) => {
  if (graphQLErrors) {
    handleGraphqlError(graphQLErrors);
  }

  if (networkError && isCancellationRequestError(networkError, operation.operationName)) return;

  if (networkError && !handleNetworkError(networkError, operation.operationName)) {
    notifyErrorFromResponse([window.I18n.errorSomethingWentWrong]);
  }
});

export const handleGraphqlError = (errors: ReadonlyArray<GraphQLFormattedError>) => {
  errors.map(({ message, locations, path }) =>
    console.error(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`)
  );
};

/**
 * Logs the error and notifies the user if should
 * returns true if the error was notified
 * @param error
 */
export const handleNetworkError = (error: NetworkError, operationName?: string): boolean => {
  console.error("[Network error]", error, { operationName });
  const serverError = error as ServerError;

  if (redirectToLoginIfSessionExpired(serverError?.statusCode)) {
    return true;
  }

  const errorResult = serverError.result as ServerErrorResult;
  const errorMessages = errorResult?.error?.message;

  if (shouldNotifyFromResponse(serverError) && errorMessages) {
    notifyErrorFromResponse([errorMessages]);
    return true;
  }

  return false;
};

const shouldNotifyFromResponse = (networkError: ServerError) => {
  return STATUS_CODES_TO_BE_NOTIFIED.includes(networkError?.statusCode);
};

const isCancellationRequestError = (networkError: NetworkError, operationName: string) => {
  const isCancellationRequestError = CANCELLATION_REQUEST_ERRORS.includes(networkError!.message);
  if (isCancellationRequestError) {
    console.warn("[Cancellation request error]", networkError!.message, { operationName });
  }

  return isCancellationRequestError;
};

const actionCableLink = new ActionCableLink({ cable });

const apolloClient: ApolloClient<NormalizedCacheObject> = new ApolloClient({
  link: ApolloLink.from([errorLink, ApolloLink.split(hasSubscriptionOperation, actionCableLink, httpLink)]),
  cache: new InMemoryCache(),
  defaultOptions: DEFAULT_APOLLO_CLIENT_OPTIONS,
});

export default apolloClient;
